import React from 'react';
import type { ClinicPhysicianResponse } from '../../services/api/types.gen';
import {
  PhysicianRosterProvider,
  type PhysicianRoster,
} from './physicianRoster';

/**
 * Stand-in form data for layout previews.
 *
 * A preview renders outside a visit, so fields scoped to a clinic have
 * nothing to read and degrade to their fallback controls — an
 * attending-physician picker shows as a plain NPI box, which is the one
 * thing the preview is meant to let an author see.
 *
 * Everything here is invented and handed to the field directly, so a preview
 * cannot read or write agency data. As more fields need standing in for, add
 * their stub here rather than teaching the field what a preview is.
 */
const PREVIEW_PHYSICIANS: ClinicPhysicianResponse[] = [
  {
    id: 'preview-physician-1',
    clinic_id: 'preview-clinic',
    agency_id: 'preview-agency',
    first_name: 'Ada',
    last_name: 'Lovelace',
    suffix: 'MD',
    npi: '1234567893',
    phone: '(555) 010-0001',
    order_delivery_preference: 'fax',
    pecos: true,
    address: '1 Example Way, Springfield',
    created_at: '2020-01-01T00:00:00Z',
    updated_at: '2020-01-01T00:00:00Z',
  },
  {
    id: 'preview-physician-2',
    clinic_id: 'preview-clinic',
    agency_id: 'preview-agency',
    first_name: 'Grace',
    last_name: 'Hopper',
    suffix: 'DO',
    npi: '1987654320',
    phone: '(555) 010-0002',
    order_delivery_preference: 'email',
    email: 'preview@example.invalid',
    pecos: false,
    created_at: '2020-01-01T00:00:00Z',
    updated_at: '2020-01-01T00:00:00Z',
  },
  {
    id: 'preview-physician-3',
    clinic_id: 'preview-clinic',
    agency_id: 'preview-agency',
    first_name: 'Alan',
    last_name: 'Turing',
    npi: '1122334455',
    phone: '(555) 010-0003',
    order_delivery_preference: 'mail',
    comment: 'Preview data — not a real physician.',
    created_at: '2020-01-01T00:00:00Z',
    updated_at: '2020-01-01T00:00:00Z',
  },
];

/**
 * `canCreate` without a `createClinicId`: the affordance is part of what an
 * author is judging, so it stays visible, but there is no clinic to write to
 * and the dialog never opens.
 */
const PREVIEW_ROSTER: PhysicianRoster = {
  physicians: PREVIEW_PHYSICIANS,
  isLoading: false,
  error: null,
  refetch: () => {},
  canCreate: true,
  createClinicId: null,
};

/** Wraps preview content so clinic-scoped fields render with stand-in data. */
export function PreviewFormDataProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <PhysicianRosterProvider roster={PREVIEW_ROSTER}>
      {children}
    </PhysicianRosterProvider>
  );
}
